// Country & city centroid coordinates for the Map view.
// Used to place listing markers without runtime geocoding.
// Coords are approximate (capital city or geographic centroid).

// 2-letter / common-name aliases → canonical country name in COUNTRY_COORDS.
export const COUNTRY_ALIASES = {
  "NG": "Nigeria", "Naija": "Nigeria",
  "GH": "Ghana",
  "KE": "Kenya",
  "ZA": "South Africa", "RSA": "South Africa",
  "ZW": "Zimbabwe",
  "CM": "Cameroon",
  "SN": "Senegal",
  "ET": "Ethiopia",
  "UG": "Uganda",
  "TZ": "Tanzania",
  "CI": "Côte d'Ivoire", "Ivory Coast": "Côte d'Ivoire",
  "RW": "Rwanda",
  "EG": "Egypt",
  "MA": "Morocco",
  "UK": "United Kingdom", "GB": "United Kingdom", "Britain": "United Kingdom", "England": "United Kingdom",
  "US": "United States", "USA": "United States", "America": "United States",
  "CA": "Canada",
};

export const COUNTRY_COORDS = {
  // African markets
  "Nigeria": [9.082, 8.6753],
  "Ghana": [7.9465, -1.0232],
  "Kenya": [-0.0236, 37.9062],
  "South Africa": [-30.5595, 22.9375],
  "Zimbabwe": [-19.0154, 29.1549],
  "Cameroon": [7.3697, 12.3547],
  "Senegal": [14.4974, -14.4524],
  "Ethiopia": [9.145, 40.4897],
  "Uganda": [1.3733, 32.2903],
  "Tanzania": [-6.369, 34.8888],
  "Côte d'Ivoire": [7.54, -5.5471],
  "Rwanda": [-1.9403, 29.8739],
  "Egypt": [26.8206, 30.8025],
  "Morocco": [31.7917, -7.0926],
  // Diaspora markets
  "United Kingdom": [54.0, -2.5],
  "United States": [37.0902, -95.7129],
  "Canada": [56.1304, -106.3468],
};

// Major city / region centroids — sparse, mostly used for higher-fidelity placement
// when a listing has a recognisable city. Falls back to country centroid otherwise.
export const CITY_COORDS = {
  // UK areas
  "London": [51.5074, -0.1278],
  "Greater London": [51.5074, -0.1278],
  "Manchester": [53.4808, -2.2426],
  "Leeds": [53.8008, -1.5491],
  "Birmingham": [52.4862, -1.8904],
  "Liverpool": [53.4084, -2.9916],
  "Bristol": [51.4545, -2.5879],
  "Sheffield": [53.3811, -1.4701],
  "Nottingham": [52.9548, -1.1581],
  "Newcastle upon Tyne": [54.9783, -1.6178],
  "Glasgow": [55.8642, -4.2518],
  "Edinburgh": [55.9533, -3.1883],
  "Cardiff": [51.4816, -3.1791],
  "Belfast": [54.5973, -5.9301],
  // US areas
  "New York": [40.7128, -74.006],
  "Los Angeles": [34.0522, -118.2437],
  "Chicago": [41.8781, -87.6298],
  "Houston": [29.7604, -95.3698],
  "Atlanta": [33.749, -84.388],
  "Washington": [38.9072, -77.0369],
  "Boston": [42.3601, -71.0589],
  "Dallas": [32.7767, -96.797],
  "Miami": [25.7617, -80.1918],
  // Canada areas
  "Toronto": [43.6532, -79.3832],
  "Montreal": [45.5017, -73.5673],
  "Vancouver": [49.2827, -123.1207],
  "Calgary": [51.0447, -114.0719],
  "Ottawa": [45.4215, -75.6972],
  // Nigeria
  "Lagos": [6.5244, 3.3792],
  "FCT (Abuja)": [9.0765, 7.3986],
  "Abuja": [9.0765, 7.3986],
  "Kano": [12.0022, 8.592],
  "Ibadan": [7.3775, 3.947],
  "Port Harcourt": [4.8156, 7.0498],
  "Rivers": [4.8156, 7.0498],
  "Oyo": [7.85, 3.93],
  // Ghana
  "Greater Accra": [5.6037, -0.187],
  "Accra": [5.6037, -0.187],
  "Ashanti": [6.7459, -1.5209],
  "Kumasi": [6.6885, -1.6244],
  // South Africa
  "Cape Town": [-33.9249, 18.4241],
  "Johannesburg": [-26.2041, 28.0473],
  "Durban": [-29.8587, 31.0218],
  "Pretoria": [-25.7479, 28.2293],
  "Gauteng": [-26.2708, 28.1123],
  // Kenya
  "Nairobi": [-1.2921, 36.8219],
  "Mombasa": [-4.0435, 39.6682],
  // Zimbabwe
  "Harare": [-17.8252, 31.0335],
  "Bulawayo": [-20.1457, 28.5873],
  // Senegal
  "Dakar": [14.7167, -17.4677],
  // Ethiopia
  "Addis Ababa": [9.03, 38.74],
  // Cameroon
  "Douala": [4.0511, 9.7679],
  "Yaoundé": [3.848, 11.5021],
  // Côte d'Ivoire
  "Abidjan": [5.3599, -4.0083],
  // Uganda
  "Kampala": [0.3476, 32.5825],
  // Rwanda
  "Kigali": [-1.9706, 30.1044],
  // Tanzania
  "Dar es Salaam": [-6.7924, 39.2083],
  // Egypt
  "Cairo": [30.0444, 31.2357],
  // Morocco
  "Casablanca": [33.5731, -7.5898],
  "Rabat": [34.0209, -6.8416],
};

// Returns [lat, lng] for a listing, deterministically jittered so multiple
// listings sharing the same city don't perfectly overlap.
export function listingCoords(listing) {
  // Exact pin always wins (Pro sellers can drop a marker).
  const lat = typeof listing.lat === "number" ? listing.lat : parseFloat(listing.lat);
  const lng = typeof listing.lng === "number" ? listing.lng : parseFloat(listing.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];

  const city = (listing.city || "").trim();
  const rawCountry = (listing.country || "").trim();
  const country = COUNTRY_COORDS[rawCountry] ? rawCountry : (COUNTRY_ALIASES[rawCountry] || rawCountry);
  const base = CITY_COORDS[city] || COUNTRY_COORDS[country];
  if (!base) return null;
  // Stable hash-based jitter so the same listing always lands in the same spot
  // but neighbours don't stack. Range ≈ ±0.04° (~4km).
  let h = 0;
  const id = listing.listing_id || `${city}${country}`;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) | 0;
  const jx = (((h & 0xffff) / 0xffff) - 0.5) * 0.08;
  const jy = ((((h >> 16) & 0xffff) / 0xffff) - 0.5) * 0.08;
  return [base[0] + jx, base[1] + jy];
}
